import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sendTemplatesApi } from '@/lib/api';

export function useSendTemplates() {
  return useQuery({
    queryKey: ['send-templates'],
    queryFn: () => sendTemplatesApi.list(),
  });
}

export function useSendTemplate(id: string | undefined) {
  return useQuery({
    queryKey: ['send-templates', id],
    queryFn: () => sendTemplatesApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateSendTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; subject: string; body: string }) =>
      sendTemplatesApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['send-templates'] });
    },
  });
}

export function useUpdateSendTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; subject?: string; body?: string; isActive?: boolean }) =>
      sendTemplatesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['send-templates'] });
    },
  });
}

export function useDeleteSendTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sendTemplatesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['send-templates'] });
    },
  });
}

export function useSeedDefaultTemplates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => sendTemplatesApi.seedDefaults(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['send-templates'] });
    },
  });
}

export function useSendEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { templateId?: string; contactId: string; dealId?: string; subject?: string; body?: string }) =>
      sendTemplatesApi.send(data),
    onSuccess: (_, variables) => {
      if (variables.dealId) {
        qc.invalidateQueries({ queryKey: ['sent-emails', 'deal', variables.dealId] });
        qc.invalidateQueries({ queryKey: ['activities', 'deal', variables.dealId] });
      }
      if (variables.contactId) {
        qc.invalidateQueries({ queryKey: ['sent-emails', 'contact', variables.contactId] });
      }
    },
  });
}

export function useDraftEmail() {
  return useMutation({
    mutationFn: (data: { contactId: string; dealId: string; emailType: string; customPrompt?: string }) =>
      sendTemplatesApi.draft(data),
  });
}

export function useRenderTemplate() {
  return useMutation({
    mutationFn: (data: { contactId: string; dealId: string; templateId?: string; subject?: string; body?: string }) =>
      sendTemplatesApi.render(data),
  });
}

export function useTestEmail() {
  return useMutation({
    mutationFn: (data: { templateId?: string; contactId: string; dealId?: string; subject?: string; body?: string }) =>
      sendTemplatesApi.test(data),
  });
}

export function useSentEmailsForDeal(dealId: string | undefined) {
  return useQuery({
    queryKey: ['sent-emails', 'deal', dealId],
    queryFn: () => sendTemplatesApi.listSentForDeal(dealId!),
    enabled: !!dealId,
  });
}

export function useSentEmailsForContact(contactId: string | undefined) {
  return useQuery({
    queryKey: ['sent-emails', 'contact', contactId],
    queryFn: () => sendTemplatesApi.listSentForContact(contactId!),
    enabled: !!contactId,
  });
}

export function useCalendarLink() {
  return useQuery({
    queryKey: ['calendar-link'],
    queryFn: () => sendTemplatesApi.getCalendarLink(),
  });
}

export function useUpdateCalendarLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (calendarLink: string) => sendTemplatesApi.updateCalendarLink(calendarLink),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['calendar-link'] });
    },
  });
}
